import './SalesDashboardProfitCommissions.css';
import { useContext, useState } from 'react';
import { UnstyledButton, Paper, rem, Modal, Button, Table, Badge, ScrollArea, Loader, Center } from '@mantine/core';
import {
  IconChevronDown,
  IconChevronUp,
  IconCurrencyDollar,
  IconReceipt,
  IconEye,
} from '@tabler/icons-react';
import { useFetch } from '@mantine/hooks';
import AuthContext from '../../contexts/AuthContext';

export function SalesDashboardProfitCommissions() {
  const { authToken } = useContext(AuthContext)
  const url = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/dashboard-comission`;
  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  };
  
  const { data, loading } = useFetch(
    url, config
  );

  const { commissions = {} } = data || {};

  const [currentYear, setCurrentYear] = useState(new Date().getFullYear().toString());
  const [monthIndex, setMonthIndex] = useState(new Date().getMonth()); // 0-11
  const [modalOpened, setModalOpened] = useState(false);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
  ];

  // Helper para convertir index 0 -> "01", 9 -> "10", etc.
  const formatMonthKey = (index) => (index + 1).toString().padStart(2, '0');

  const handleMonthChange = (increment) => {
    setMonthIndex((prevIndex) => {
      let newIndex = prevIndex + increment;
      if (newIndex < 0) {
        // Opcional: Podrías bajar el currentYear aquí si existiera en el objeto
        newIndex = 11;
      } else if (newIndex > 11) {
        newIndex = 0;
      }
      return newIndex;
    });
  };

  // Obtener la data del mes actual de forma segura
  const monthKey = formatMonthKey(monthIndex);
  const monthData = commissions[currentYear]?.[monthKey] || { USD: 0, ARS: 0, details: [] };

  const currentMonth = monthNames[monthIndex];
  const currentMonthARS = monthData.ARS;
  const currentMonthUSD = monthData.USD;
  const currentMonthDetails = monthData.details;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency) => {
    return currency === 'USD'
      ? `USD $${amount.toFixed(2)}`
      : `ARS $${amount.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };
  
  if (loading) return <Center mih='350px'>
    <Loader />
    <h3>Cargando comisiones...</h3>
  </Center>
  
  if (!data) return <></>
  
  return (
    <>
      <div className="salesDashboardProfitCommissions-root">
        <div className="salesDashboardProfitCommissions-header">
          <div className="salesDashboardProfitCommissions-title-section">
            <IconReceipt size={24} className="salesDashboardProfitCommissions-title-icon" />
            <h3 className="salesDashboardProfitCommissions-title">Tus Comisiones</h3>
          </div>

          <div className="salesDashboardProfitCommissions-controls">
            <UnstyledButton
              className="salesDashboardProfitCommissions-control"
              onClick={() => handleMonthChange(-1)}
            >
              <IconChevronUp style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </UnstyledButton>

            <div className="salesDashboardProfitCommissions-date">
              <p className="salesDashboardProfitCommissions-month">{currentMonth} {currentYear}</p>
            </div>

            <UnstyledButton
              className="salesDashboardProfitCommissions-control"
              onClick={() => handleMonthChange(1)}
            >
              <IconChevronDown style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
            </UnstyledButton>
          </div>
        </div>

        <div className="salesDashboardProfitCommissions-cards-container">
          <Paper className="salesDashboardProfitCommissions-card salesDashboardProfitCommissions-card-ars">
            <div className="salesDashboardProfitCommissions-card-header">
              <span className="salesDashboardProfitCommissions-card-label">Pesos Argentinos</span>
              <span className="salesDashboardProfitCommissions-currency-badge">ARS</span>
            </div>
            <div className="salesDashboardProfitCommissions-card-value">
              <span className="salesDashboardProfitCommissions-currency-symbol">$</span>
              <span className="salesDashboardProfitCommissions-amount">
                {currentMonthARS.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </Paper>

          <Paper className="salesDashboardProfitCommissions-card salesDashboardProfitCommissions-card-usd">
            <div className="salesDashboardProfitCommissions-card-header">
              <span className="salesDashboardProfitCommissions-card-label">Dólares</span>
              <span className="salesDashboardProfitCommissions-currency-badge">USD</span>
            </div>
            <div className="salesDashboardProfitCommissions-card-value">
              <IconCurrencyDollar className="salesDashboardProfitCommissions-dollar-icon" />
              <span className="salesDashboardProfitCommissions-amount">
                {currentMonthUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </Paper>
        </div>

        <Button
          className="salesDashboardProfitCommissions-details-btn"
          onClick={() => setModalOpened(true)}
          leftSection={<IconEye size={18} />}
          disabled={currentMonthDetails.length === 0}
        >
          Ver Detalles ({currentMonthDetails.length})
        </Button>
      </div>

      <Modal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        title={`Detalles de Comisiones - ${currentMonth} ${currentYear}`}
        size="70%"
        className="salesDashboardProfitCommissions-modal"
        styles={{
          title: {
            fontSize: 'var(--fs-h4)',
            color: '#2C4B58',
            fontWeight: 600,
          },

        }}
      >
        <div className="salesDashboardProfitCommissions-modal-content">
          <div className="salesDashboardProfitCommissions-modal-summary">
            <div className="salesDashboardProfitCommissions-summary-item">
              <span className="salesDashboardProfitCommissions-summary-label">Total Mes ARS:</span>
              <span className="salesDashboardProfitCommissions-summary-value">
                ${currentMonthARS.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            <div className="salesDashboardProfitCommissions-summary-item">
              <span className="salesDashboardProfitCommissions-summary-label">Total Mes USD:</span>
              <span className="salesDashboardProfitCommissions-summary-value">
                ${currentMonthUSD.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          </div>

          <ScrollArea.Autosize mah={400} className="salesDashboardProfitCommissions-table-container">
            <Table className="salesDashboardProfitCommissions-table" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Fecha</Table.Th>
                  <Table.Th>Estudiante</Table.Th>
                  <Table.Th>Curso</Table.Th>
                  <Table.Th>Tipo</Table.Th>
                  <Table.Th className="salesDashboardProfitCommissions-commission-th">Comisión</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {currentMonthDetails.map((detail, index) => (
                  <Table.Tr key={index}>
                    <Table.Td>{formatDate(detail.date)}</Table.Td>
                    <Table.Td>{detail.student}</Table.Td>
                    <Table.Td className="salesDashboardProfitCommissions-course-cell">{detail.course}</Table.Td>
                    <Table.Td>
                      <Badge className="salesDashboardProfitCommissions-type-badge" size="sm" variant="light">
                        {detail.type}
                      </Badge>
                    </Table.Td>
                    <Table.Td className="salesDashboardProfitCommissions-commission-cell">
                      {formatCurrency(detail.commission, detail.currency)}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        </div>
      </Modal>
    </>
  );
}